/*
 *+------------------------------------------------------------------------+
 *| Licensed Materials - Property of IBM
 *| IBM Cognos Products: BI Dashboard
 *| (C) Copyright IBM Corp. 2018, 2020
 *|
 *| US Government Users Restricted Rights - Use, duplication or disclosure
 *| restricted by GSA ADP Schedule Contract with IBM Corp.
 *+------------------------------------------------------------------------+
 */
import MinMaxCollector from './MinMaxCollector';
import MeasureIndex from './MeasureIndex';
import CrosshairsViewCtrl from './CrosshairsViewCtrl';
import RenderCrosshairsStep from './RenderCrosshairsStep';

/**
 * The Crosshairs feature is used to collect data for and render crosshairs on a visualization.
 **/
class CrosshairsFeature {
	constructor(widgetApi, dashboardApi) {
		this.widgetApi = widgetApi;
		this.dashboardApi = dashboardApi;
		this.isNewQueryApiEnabled = this._isNewQueryApiEnabled();

		this._activated = false;

		this.measureIndexes = {};
		this.rangePercent = 5;	//Points will be highlighted in a range that is rangePercent% of the domain wide.

		this.viewCtrl = new CrosshairsViewCtrl(this, widgetApi, dashboardApi);

		this._featureTags=[];
	}

	destroy() {
		this.viewCtrl.destroy();
		this.viewCtrl=null;
	}

	_isNewQueryApiEnabled() {
		//TODO: This function can be removed when the feature flag is removed.
		return !this.dashboardApi.getGlassCoreSvc('.FeatureChecker').checkValue('dashboard', 'queryApi', 'disabled');
	}

	//Identify this feature as a plugin for the resultDataReaderPlugin and visEventHandler.
	//NOTE: See ResultDataReader and VisEventHandler for expected api's
	getFeatureTags() {
		return this._featureTags;
	}

	getAPI() {
		return {
			//Host applications can use these api's to turn on/off crosshairs
			activate: this.activate.bind(this),	//set up callbacks for synchronizing.
			deactivate: this.deactivate.bind(this),
			isActivated: this.isActivated.bind(this),
			isSupported: this.isSupported.bind(this),

			//ResultDataReader plugin api's
			onDataStart: this.onDataStart.bind(this),
			onDataRow: this.onDataRow.bind(this),
			onDataDone: this.onDataDone.bind(this),

			//Creating Popover specific API's
			getCrossHairMatchingPoints: this.getCrossHairMatchingPoints.bind(this),
			renderPopOver: this.renderPopOver.bind(this),

			//Crosshairs-specific api's
			getDataPoints: this.getDataPoints.bind(this),
			renderCrosshairs: this.renderCrosshairs.bind(this),	//call used to 'render in synch'
			getOptimalLineValue: this.getOptimalLineValue.bind(this) // call used to get the line value for default position
		};
	}

	/**
	 * @returns true if this visualization supports crosshairs
	 */
	isSupported() {
		//NOTE: When changing visApi to visualizationApi, we should
		//implement a supportsDecoration on the new decorator api
		//so that we can get this information directly from vipr.
		const visId = this.widgetApi.getVisId();

		const supportedVisTypes = [
			'com.ibm.vis.rave2bundlearea',
			'com.ibm.vis.rave2bundlebar',
			'com.ibm.vis.rave2bundlecolumn',
			'com.ibm.vis.rave2bundlestackedbar',
			'com.ibm.vis.rave2bundlestackedcolumn',
			'com.ibm.vis.rave2line',
			'com.ibm.vis.rave2point',
			'com.ibm.vis.rave2bundlewaterfall'
		];

		return supportedVisTypes.some((supportedVisType) => supportedVisType===visId);
	}

	isActivated() {
		return this._activated;
	}

	activate(options = {}) {
		//When activated, this feature will be executed as a plugin to the ResultsDataReader render sequence task
		//(to cross-reference datapoints with measure values)
		this._activated = true;
		this._featureTags=['ResultDataReaderPlugin'];
		this.viewCtrl.activate(options);
	}

	deactivate() {
		//When activated, this feature will be executed as a plugin to the ResultsDataReader render sequence task
		//(to cross-reference datapoints with measure values)
		this._activated = false;
		this.viewCtrl.deactivate();
	}

	/**
	 * Process Query result based on the passed options to constructor.
	 * For instance, limit the rows of DataPoints so that in the response, for
	 * specified dataitem, 'product', there will be only three tuples returned
	 *
	 * @returns A promise which resolves with the resulting statistics. (This will be useful to be symmetrical with the query promise for totals).
	 */
	onDataStart(queryResultsData, rowHeaders) {
		this.queryResultsData=queryResultsData;

		//We use a minMax collector to collect min/max for each measure.
		this.minMaxCollector = new MinMaxCollector();
		this.minMaxCollector.reset();

		this.valueSlotMeasureCount = rowHeaders.valueSlotMeasureCount;
		this.measureItemHeaders = rowHeaders.measureItemHeaders;
		this.categoryItemHeaders = rowHeaders.categoryItemHeaders;

		this.measureItemHeaders.forEach((measureItemHeader) => {
			this.measureIndexes[measureItemHeader.dataItemUniqueId]=new MeasureIndex(measureItemHeader.dataItemUniqueId);
		});
	}

	onDataRow(rowIdx) {
		let rowMinMaxEntries=[];
		this.measureItemHeaders.forEach((measureItemHeader) => {
			const minMaxEntry=this.minMaxCollector.getMinMaxEntry(measureItemHeader.dataItemUniqueId);
			const value = this._getCellValue(this.queryResultsData, rowIdx + measureItemHeader.valueItemIndex, measureItemHeader.index) || 0;
			this.minMaxCollector.updateMinMaxEntry(minMaxEntry, value);
			rowMinMaxEntries.push({ dataItemUniqueId: measureItemHeader.dataItemUniqueId, value: value });
		});

		this._saveMeasureValuesForRow(rowIdx, rowMinMaxEntries);
	}

	onDataDone() {
	}

	getCrossHairMatchingPoints(...args) {
		if (!this.viewCtrl) {
			// destroyed
			return;
		}
		return this.viewCtrl.getCrossHairMatchingPoints(...args);
	}

	renderPopOver(...args) {
		if (!this.viewCtrl) {
			// destroyed
			return;
		}
		return this.viewCtrl.renderPopOver(...args);
	}

	/**
	 * returns the data points that are associated with the given dataItemUniqueId for this measure (ordinal)
	 * @param measureDataItemUniqueId - the dataItemUniqueId of the measure (ordinal) we're rendering crosshairs for
	 * @param measureValue - the value we want associated DataPoints for.
	 * @returns the set of DataPoints that are associated with this measurevalue.
	 */
	getDataPoints(measureDataItemUniqueId, measureValue) {
		let points=[];
		const thisMeasureIndex = this.measureIndexes[measureDataItemUniqueId];
		const minMaxEntry=this.minMaxCollector.getMinMaxEntry(measureDataItemUniqueId);
		if (!thisMeasureIndex || !minMaxEntry) {
			return points;
		}

		const valueRange = (minMaxEntry.maxUse - minMaxEntry.minUse) * (this.rangePercent / 100);

		return thisMeasureIndex.getEntries(measureValue, valueRange);
	}

	//Process the categorical items in a row returning their values as an array... eg [2005, Camping, Fax]
	getRowTuple(rowIdx) {
		let tuple=[];
		this.categoryItemHeaders.columnIndexes.forEach((columnIndex) => {
			//Note: getValue returns the tuple of categorical values at that cell index for stacked items.
			tuple=tuple.concat(this._getCellValueObject(this.queryResultsData, rowIdx, columnIndex));
		});
		return tuple;
	}

	//TODO: Remove this function when  isNewQueryApiEnabled is removed.
	_getCellValueObject(queryResult, row, column) {
		return this.isNewQueryApiEnabled ? queryResult.getValue(row, column) : queryResult.getCellValue(row, column);
	}

	//TODO: Remove this function when  isNewQueryApiEnabled is removed.
	_getCellValue(queryResult, row, column) {
		const cellValueObject = this._getCellValueObject(queryResult, row, column);
		if (this.isNewQueryApiEnabled) {
			return (cellValueObject && cellValueObject.getValue) ? cellValueObject.getValue() : cellValueObject.value;
		} else {
			return (cellValueObject && cellValueObject.getValue) ? cellValueObject.getValue() : cellValueObject.v;
		}
	}

	getItemValue(rowIdx, dataItemUniqueId) {
		return this.measureIndexes[dataItemUniqueId] &&
				this.measureIndexes[dataItemUniqueId].getValue(rowIdx);
	}

	_saveMeasureValuesForRow(rowIdx, rowMinMaxEntries) {
		//for each row, if the categorical tuple for that row is selected, add it to selection minMax.
		if (this.measureIndexes) {
			rowMinMaxEntries.forEach((rowMinMaxEntry) => {
				const thisMeasureIndex = this.measureIndexes[rowMinMaxEntry.dataItemUniqueId];
				thisMeasureIndex.add(rowIdx, rowMinMaxEntry.value);
			});
		}
	}

	//TODO: If we ever support crosshairs on multi-layer charts, we'll need to do this differently.
	getItemToDecorate() {
		const content = this.dashboardApi.getCanvas().getContent(this.widgetApi.getId());
		const mappingInfoList = content.getFeature('Visualization').getSlots().getMappingInfoList();
		const ordinalDataItemAPI = mappingInfoList.find(item => item.slot.getDefinition().getType() === 'ordinal');
		if (ordinalDataItemAPI) {
			const ordinalSlotIndex=content.getFeature('Visualization').getSlots().getMappedSlotList().findIndex(
				(slotAPI) => { return slotAPI.getDefinition().getType() === 'ordinal'; });

			return {
				itemIndex: ordinalSlotIndex,
				sourceItemUniqueId: ordinalDataItemAPI.dataItem.getId(),
				itemId: ordinalDataItemAPI.dataItem.getColumnId(),
				format: ordinalDataItemAPI.dataItem.getFormat()
			};
		}
	}

	/**
	* Return the feature extra render sequence steps
	*/
	getExtraRenderSequenceSteps() {
		return [{
			id: 'renderVisCrosshairs',
			dependencies:['render'],
			module: RenderCrosshairsStep,
			moduleOptions:{
				visCrosshairFeature: this
			}
		}];
	}

	/**
	 * gets the optimal line value
	 * if the previous value is still within range of current measure's range, we use the previous value of the line (eg. a render from resizing) 
	 * otherwise we will calculate an optimal value from min max collector by taking the average between min and max
	 * @return {number} optimalValue
	 */
	getOptimalLineValue() {
		const itemToDecorate = this.getItemToDecorate();
		const previousValue = this.viewCtrl.getPreviousValue();
		let optimalValue;
		if (itemToDecorate) {
			const itemUniqueId = itemToDecorate.sourceItemUniqueId;
			if (this.minMaxCollector && this.minMaxCollector.minMaxTable && this.minMaxCollector.minMaxTable[itemUniqueId]) {
				const itemStat = this.minMaxCollector.minMaxTable[itemUniqueId];
				// if the previous value is still within range of current measure's range, we use the previous value of the line (eg. a render from resizing)
				// otherwise we will calculate an optimal value from min max collector by taking the average between min and max
				// this case of defined previous value can be hit when 
				// a) user drag the compare line to xx value, then toggle to hide and toggle to show compare line
				// b) user changes the data in the vis (eg. apply filters, top bottom, properties)
				if ((previousValue || previousValue === 0) && previousValue <= itemStat.maxUse && previousValue >= itemStat.minUse) {
					optimalValue = previousValue;
				} else {
					// default to the middle of the range for easier discovery of the compare line
					optimalValue = (itemStat.maxUse + itemStat.minUse) / 2;
				}
			}
		}
		return optimalValue;
	}

	/**
	 * renderCrosshairs at the specified value (for now, the value is single dimension)
	 * @param {number} value - the measure value to render the crosshairs at.
	 */
	renderCrosshairs(value) {
		if (!this.viewCtrl) {
			// destroyed
			return Promise.resolve();
		}
		let optimalValue = value;
		// when value is falsy but not zero, calculate an optimal value to make sure the value is within the range.
		// The optimal value will be previous value if previous value is defined and still within the range
		if (!(value || value === 0)) {
			optimalValue = this.getOptimalLineValue();
		}
		if (this.isSupported() && this.isActivated()) {
			return this.viewCtrl.renderCrosshairs(optimalValue);
		} else {
			// clear crosshairs since the new vis type no longer supports and the value is set as previous value so we can clear highlights next time
			this.viewCtrl.clearCrosshairs();
			this.viewCtrl.setPreviousValue(optimalValue);
			return;
		}
	}

}

export default CrosshairsFeature;



// WEBPACK FOOTER //
// ./src/js/lib/@waca/vis-crosshairs/src/js/CrosshairsFeature.js